/* cc-category-rail — sliding active pill + product grid swap.
   First category is server-rendered; others come from
   /collections/<handle>?section_id=cc-card-batch (server-rendered THE cards, real stars), cached per handle.
   Cards animate in staggered; reduced-motion renders instantly. */

class CCCategoryRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cc-category-rail__track');
    this.thumb = this.querySelector('.cc-category-rail__thumb');
    this.grid = this.querySelector('.cc-category-rail__grid');
    this.more = this.querySelector('.cc-category-rail__more a');
    this.pills = Array.from(this.querySelectorAll('.cc-category-rail__pill'));
    if (!this.pills.length) return;

    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.cache = {};

    // Column layout: keep cards in a flat list, distribute round-robin
    // into independent flex columns (expanding card only pushes its own column)
    this.cards = Array.from(this.grid.querySelectorAll('.cc-cprod'));
    this.distribute();

    // Cluster carousel
    this.tracks = Array.from(this.querySelectorAll('.cc-category-rail__track'));
    // single-pill clusters render as plain title+icon, not a capsule (Tony 2026-07-11)
    this.tracks.forEach((t) => t.classList.toggle('cc-category-rail__track--solo', t.querySelectorAll('.cc-category-rail__pill').length === 1));
    this.sidePrev = this.querySelector('.cc-category-rail__side--prev');
    this.sideNext = this.querySelector('.cc-category-rail__side--next');
    this.cluster = 0;
    this.querySelector('.cc-category-rail__arrow--prev')?.addEventListener('click', () => this.setCluster(this.cluster - 1, -1));
    this.querySelector('.cc-category-rail__arrow--next')?.addEventListener('click', () => this.setCluster(this.cluster + 1, 1));
    this.sidePrev?.addEventListener('click', () => this.setCluster(this.cluster - 1, -1));
    this.sideNext?.addEventListener('click', () => this.setCluster(this.cluster + 1, 1));
    this.updateSides();

    // swipe left/right on the pill row rotates the wheel (mobile)
    const nav = this.querySelector('.cc-category-rail__nav');

    if (nav) this.initWheelDrag(nav);

    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => this.select(pill));
      // desktop: intent shows up in the pointer ~100-300ms before the
      // click — spend it on the fetch (Tony 2026-08-02)
      pill.addEventListener('pointerenter', () => this.prefetch(pill.dataset.handle), { passive: true });
    });
    this.sidePrev?.addEventListener('pointerenter', () => this.warmNeighbors(), { passive: true });
    this.sideNext?.addEventListener('pointerenter', () => this.warmNeighbors(), { passive: true });
    window.addEventListener('resize', () => {
      this.placeThumb(this.activePill());
      if (this.colCount() !== this.cols) this.distribute();
    });

    // Thumb needs laid-out pill positions (fonts can shift them)
    requestAnimationFrame(() => this.placeThumb(this.activePill()));
    if (document.fonts?.ready) document.fonts.ready.then(() => this.placeThumb(this.activePill()));

    // Warm the two clusters either side of the opening one once the
    // page is idle. A swap that lands on a warm handle renders in the
    // same frame — no spinner, no dim, no dead beat to design around.
    this.idle(() => this.warmNeighbors());
  }

  /* ---- Prefetch (Tony 2026-08-02) ------------------------------------
     The swap used to wait ~250ms on a cold Section-Rendering request
     with the previous category's products still on screen. Rather than
     dress that pause up, remove it: warm the neighbours the user is
     most likely to reach next. Skipped entirely on Save-Data and 2g so
     we never spend someone's metered bytes on a guess. */
  idle(fn) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 1200);
  }

  thrifty() {
    const c = navigator.connection;
    return !!c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''));
  }

  prefetch(handle) {
    if (!handle || this.cache[handle] || this.thrifty()) return;
    this.fetchProducts(handle);
  }

  warmNeighbors() {
    const n = this.tracks.length;
    if (n < 2) return;
    [(this.cluster - 1 + n) % n, (this.cluster + 1) % n].forEach((i) => {
      const pill = this.tracks[i]?.querySelector('.cc-category-rail__pill');
      if (pill) this.prefetch(pill.dataset.handle);
    });
  }

  /* Mobile wheel: ALL cluster pills live on the ring permanently —
     active at center, ±1 peeking at the screen edges, the rest parked
     off-screen at ring positions. Drags shift the whole ring 1:1 with
     the finger; commits glide every pill one slot over and re-anchor.
     Nothing ever pops in or loads late. */
  initWheelDrag(nav) {
    const mobile = window.matchMedia('(max-width: 749px)');
    // debug hook: ?ccwheel=force lets desktop DevTools/QA drive the wheel
    const forced = /ccwheel=force/.test(location.search);
    const wheelOn = () => mobile.matches || forced;
    const PEEK = 72; // MIN px of a neighbor visible when the edge-cap kicks in
    const NEIGHBOR_GAP = 28; // px between the active title and a parked neighbor
    let drag = null;
    let parked = false;
    let retries = 0;

    const n = () => this.tracks.length;

    // signed shortest ring distance from the active cluster
    const ringD = (i, activeIdx) => {
      let d = (i - activeIdx) % n();
      if (d > n() / 2) d -= n();
      if (d < -n() / 2) d += n();
      return d;
    };

    // slot = parked center-offset for the ±1 stickers. Neighbors park
    // CLOSE to the active title so they stay readable (Tony round 4);
    // the edge cap keeps PEEK px visible for extra-wide titles. The
    // active pill renders at scale(1.15) without changing layout width,
    // so its visual width is offsetWidth * 1.15.
    // activeIdx is EXPLICIT: commits glide toward the INCOMING cluster's
    // geometry, so the post-settle re-park lands on identical positions
    // (computing against this.cluster mid-flight caused a visible jolt
    // when slots were re-measured after arrival — Tony round 5).
    const slotFor = (t, activeIdx) => {
      const act = this.tracks[activeIdx];
      const desired = (act.offsetWidth * 1.15) / 2 + t.offsetWidth / 2 + NEIGHBOR_GAP;
      const max = nav.clientWidth / 2 + t.offsetWidth / 2 - PEEK;
      return Math.max(Math.min(desired, max), (act.offsetWidth * 1.15) / 2 + 16);
    };
    const farStep = () => nav.clientWidth * 0.9; // spacing beyond ±1 (off-screen)

    const baseFor = (t, i, activeIdx) => {
      const d = ringD(i, activeIdx);
      if (d === 0) return 0;
      return Math.sign(d) * (slotFor(t, activeIdx) + (Math.abs(d) - 1) * farStep());
    };

    const setPos = (t, tx, slot) => {
      const near = Math.max(0, 1 - Math.abs(tx) / Math.max(slot, 1));
      t.style.setProperty('--wx', Math.round(tx) + 'px');
      t.style.setProperty('--wy', Math.round(9 * (1 - near)) + 'px');
      t.style.setProperty('--wr', (Math.max(-24, Math.min(24, tx * 0.03))).toFixed(2) + 'deg');
      t.style.setProperty('--wo', (0.55 + 0.45 * near).toFixed(3));
    };

    // place the whole ring for a given active index, shifted by x
    const applyRing = (x, activeIdx) => {
      if (!nav.clientWidth) return;
      this.tracks.forEach((t, i) => {
        setPos(t, baseFor(t, i, activeIdx) + x, slotFor(t, activeIdx));
      });
    };

    const unstage = () => {
      this.tracks.forEach((t) => {
        t.classList.remove('cc-category-rail__track--staged');
        ['--wx', '--wy', '--wr', '--wo'].forEach((v) => t.style.removeProperty(v));
      });
    };

    const restage = () => {
      unstage();
      parked = false;
      if (!wheelOn() || n() < 2) {
        nav.classList.remove('cc-category-rail__nav--wheeling');
        this.isWheel = false;
        return;
      }
      nav.classList.add('cc-category-rail__nav--wheeling');
      this.isWheel = true;
      this.tracks.forEach((t, i) => {
        if (i !== this.cluster) t.classList.add('cc-category-rail__track--staged');
      });
      // widths are only real once the CSS has landed — retry, don't park blind
      if (!this.tracks[this.cluster].offsetWidth || !nav.clientWidth) {
        if (retries < 12) {
          retries += 1;
          setTimeout(restage, 150);
        }
        return;
      }
      retries = 0;
      // re-park INSTANTLY: with transitions live, even a few px of
      // re-measured delta animates lazily after the settle (the
      // "pills move again afterwards" complaint)
      nav.classList.add('cc-category-rail__nav--dragging');
      applyRing(0, this.cluster);
      void nav.offsetWidth;
      setTimeout(() => nav.classList.remove('cc-category-rail__nav--dragging'), 50);
      parked = true;
    };
    this.restageWheel = restage;

    const commit = (dir) => {
      this.wheelBusy = true;
      const targetIdx = (this.cluster + dir + n()) % n();
      const incoming = this.tracks[targetIdx];
      // the glide is ~500ms of free time — start the fetch now so the
      // catalog is already in hand when the pill lands
      this.prefetch(incoming.querySelector('.cc-category-rail__pill')?.dataset.handle);
      incoming.classList.add('cc-category-rail__track--arriving'); // whitens on the way in
      applyRing(0, targetIdx); // every pill glides one slot over — exact targets

      // Swap on TRANSITIONEND, not a timer: a fixed 480ms raced the
      // 500ms glide — under real-device frame jank the pill was still
      // short of center when the timer preempted it (visible pop).
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        incoming.removeEventListener('transitionend', onEnd);
        incoming.style.animation = 'none'; // it already arrived
        this.setCluster(targetIdx, dir);
        restage(); // re-anchor bases (identical positions — invisible)
        setTimeout(() => {
          incoming.classList.remove('cc-category-rail__track--arriving');
          incoming.style.animation = '';
        }, 250);
        this.wheelBusy = false;
      };
      const onEnd = (e) => {
        if (e.target === incoming && e.propertyName === 'transform') finish();
      };
      if (this.reducedMotion) {
        finish();
      } else {
        incoming.addEventListener('transitionend', onEnd);
        setTimeout(finish, 900); // safety net if the event never fires
      }
    };

    // tapping a peeking sticker rolls the wheel to it
    nav.addEventListener(
      'click',
      (e) => {
        if (!wheelOn()) return;
        const track = e.target.closest('.cc-category-rail__track--staged');
        if (!track || this.wheelBusy) return;
        const d = ringD(this.tracks.indexOf(track), this.cluster);
        if (Math.abs(d) !== 1) return; // only the visible neighbors
        e.preventDefault();
        e.stopPropagation();
        commit(d);
      },
      true
    );

    nav.addEventListener(
      'pointerdown',
      (e) => {
        if (!wheelOn() || n() < 2 || this.wheelBusy) return;
        if (!parked) restage(); // self-heal before the first gesture
        // touch down is the earliest honest signal a swap is coming
        this.warmNeighbors();
        drag = { x0: e.clientX, lastX: e.clientX, lastT: e.timeStamp, v: 0, x: 0, rx: 0, live: false, id: e.pointerId, D: slotFor(this.tracks[this.cluster], this.cluster) };
      },
      { passive: true }
    );

    nav.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x0;
      // 6px slop before the wheel grabs — keeps pill taps working
      if (!drag.live) {
        if (Math.abs(dx) < 6) return;
        drag.live = true;
        try { nav.setPointerCapture(drag.id); } catch (err) { /* noop */ }
        nav.classList.add('cc-category-rail__nav--dragging');
      }
      const dt = e.timeStamp - drag.lastT;
      if (dt > 0) drag.v = (e.clientX - drag.lastX) / dt;
      drag.lastX = e.clientX;
      drag.lastT = e.timeStamp;
      // rubber-band: 1:1 to 60% of the travel, then growing resistance
      const soft = drag.D * 0.6;
      const a = Math.abs(dx);
      drag.x = Math.sign(dx) * Math.min(drag.D, a <= soft ? a : soft + (a - soft) * 0.3);
      if (!drag.raf) {
        const step = () => {
          if (!drag) return;
          drag.rx += (drag.x - drag.rx) * 0.28;
          applyRing(drag.rx, this.cluster);
          drag.raf = requestAnimationFrame(step);
        };
        drag.raf = requestAnimationFrame(step);
      }
    });

    const release = () => {
      if (!drag) return;
      const d = drag;
      drag = null;
      if (d.raf) cancelAnimationFrame(d.raf);
      if (!d.live) return;
      nav.classList.remove('cc-category-rail__nav--dragging'); // transitions back on
      const flick = Math.abs(d.v) > 0.35 ? (d.v < 0 ? 1 : -1) : 0;
      const settle = Math.abs(d.x) > d.D * 0.35 ? (d.x < 0 ? 1 : -1) : 0;
      const dir = flick || settle;
      if (dir) {
        commit(dir);
      } else {
        this.wheelBusy = true;
        applyRing(0, this.cluster); // spring back
        setTimeout(() => (this.wheelBusy = false), this.reducedMotion ? 0 : 480);
      }
    };
    nav.addEventListener('pointerup', release);
    nav.addEventListener('pointercancel', release);

    // park whenever layout truly settles; RO also covers breakpoint flips
    const ro = new ResizeObserver(() => {
      if (drag || this.wheelBusy) return;
      restage();
    });
    ro.observe(nav);
    window.addEventListener('load', () => {
      if (!drag && !this.wheelBusy) restage();
    });
  }

  activePill() {
    return this.querySelector('.cc-category-rail__pill[aria-selected="true"]') || this.pills[0];
  }

  setCluster(idx, dir) {
    if (this.tracks.length < 2) return;
    this.cluster = (idx + this.tracks.length) % this.tracks.length;
    this.tracks.forEach((t, i) => {
      t.style.setProperty('--cc-dir', dir);
      t.classList.toggle('cc-category-rail__track--active', i === this.cluster);
    });
    this.updateSides();
    // focusing a cluster selects its first pill (loads that catalog)
    const first = this.tracks[this.cluster].querySelector('.cc-category-rail__pill');
    if (first) {
      requestAnimationFrame(() => this.select(first, true));
    }
    // whichever way they came, the next hop is one of these two
    this.idle(() => this.warmNeighbors());
  }

  updateSides() {
    if (this.tracks.length < 2) {
      if (this.sidePrev) this.sidePrev.hidden = true;
      if (this.sideNext) this.sideNext.hidden = true;
      return;
    }
    const n = this.tracks.length;
    const prev = this.tracks[(this.cluster - 1 + n) % n];
    const next = this.tracks[(this.cluster + 1) % n];
    if (this.sidePrev) this.sidePrev.textContent = prev.dataset.clusterLabel || '';
    if (this.sideNext) this.sideNext.textContent = next.dataset.clusterLabel || '';
  }

  placeThumb(pill) {
    if (!pill) return;
    const track = pill.closest('.cc-category-rail__track');
    this.querySelectorAll('.cc-category-rail__thumb').forEach((t) => {
      t.classList.toggle('cc-category-rail__thumb--on', t.parentElement === track);
    });
    const thumb = track && track.querySelector('.cc-category-rail__thumb');
    if (!thumb) return;
    thumb.style.left = pill.offsetLeft + 'px';
    thumb.style.width = pill.offsetWidth + 'px';
  }

  async select(pill, force = false) {
    if (!force && pill.getAttribute('aria-selected') === 'true') return;
    this.pills.forEach((p) => p.setAttribute('aria-selected', p === pill ? 'true' : 'false'));
    this.placeThumb(pill);
    // wheel mode: the ring owns ALL positioning — scrollIntoView here
    // smooth-scrolled ancestors ~300ms after the settle (late shifts +
    // thumb misalignment). Desktop keeps it for the subpill scroller.
    if (!this.isWheel) {
      pill.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    const handle = pill.dataset.handle;
    if (this.more) this.more.href = `/collections/${handle}`;
    const token = (this.loadToken = Symbol());
    const cardsHTML = await this.fetchProducts(handle);
    if (this.loadToken !== token) return; // superseded by a newer click
    this.renderGrid(cardsHTML);
    // the sub-pills of this cluster are the cheapest next click there is
    this.idle(() => {
      const track = pill.closest('.cc-category-rail__track');
      track?.querySelectorAll('.cc-category-rail__pill').forEach((p) => this.prefetch(p.dataset.handle));
    });
  }

  async fetchProducts(handle) {
    /* Section Rendering API instead of products.json (2026-07-11):
       batches arrive as THE card server-rendered by cc-card-batch —
       real Judge.me stars (metafields never reach products.json, which
       left switched-in categories star-less). Cache stores the HTML.
       inflight[] dedupes a prefetch and a click racing the same handle. */
    if (this.cache[handle]) return this.cache[handle];
    this.inflight = this.inflight || {};
    if (this.inflight[handle]) return this.inflight[handle];
    this.inflight[handle] = (async () => {
      try {
        const res = await fetch(`/collections/${handle}?section_id=cc-card-batch`);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const cards = Array.from(doc.querySelectorAll('.cc-cprod')).slice(0, this.limit);
        this.cache[handle] = cards.map((c) => c.outerHTML).join('');
      } catch {
        this.cache[handle] = '';
      }
      delete this.inflight[handle];
      return this.cache[handle];
    })();
    return this.inflight[handle];
  }

  colCount() {
    const style = getComputedStyle(this);
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    return parseInt(style.getPropertyValue(isDesktop ? '--cc-rail-cols' : '--cc-rail-cols-mobile'), 10) || (isDesktop ? 4 : 2);
  }

  distribute() {
    this.cols = this.colCount();
    this.grid.classList.add('cc-category-rail__grid--cols');
    this.grid.innerHTML = '';
    const columns = [];
    for (let i = 0; i < this.cols; i++) {
      const col = document.createElement('div');
      col.className = 'cc-category-rail__col';
      columns.push(col);
      this.grid.appendChild(col);
    }
    this.cards.forEach((card, i) => columns[i % this.cols].appendChild(card));
  }

  renderGrid(cardsHTML) {
    const holder = document.createElement('div');
    holder.innerHTML = cardsHTML;
    this.cards = Array.from(holder.querySelectorAll('.cc-cprod'));
    this.distribute();

    if (this.reducedMotion) return;
    const cards = this.cards;
    cards.forEach((card) => card.classList.add('cc-category-rail__card--enter'));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        cards.forEach((card, i) => {
          card.style.transitionDelay = i * 40 + 'ms';
          card.classList.add('cc-category-rail__card--in');
          card.addEventListener(
            'transitionend',
            () => {
              card.style.transitionDelay = '';
              card.classList.remove('cc-category-rail__card--enter', 'cc-category-rail__card--in');
            },
            { once: true }
          );
        });
      })
    );
  }
}

customElements.define('cc-category-rail', CCCategoryRail);
